/*
 * Toast notification system — lightweight, auto-dismissing feedback toasts.
 * Uses React context so any component can push a toast without prop drilling.
 *
 * Usage:
 *   import { useToast } from "./ToastProvider";
 *   const toast = useToast();
 *   toast.success("Changes applied");
 *   toast.error("Failed to reach node");
 *   toast.info("Polling resumed");
 *
 * ! file components/ToastProvider.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2025
 */

import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { FiCheckCircle, FiAlertTriangle, FiInfo, FiX } from "react-icons/fi";

const ToastContext = createContext(null);

let toastId = 0;

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}

export default function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    clearTimeout(timers.current[id]);
    delete timers.current[id];
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (level, message, duration = 5000) => {
      const id = ++toastId;
      setToasts((prev) => [...prev.slice(-4), { id, level, message }]); // keep max 5
      if (duration > 0) {
        timers.current[id] = setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss]
  );

  const api = {
    success: (msg, dur) => push("success", msg, dur),
    error: (msg, dur) => push("error", msg, dur ?? 8000),
    warning: (msg, dur) => push("warning", msg, dur ?? 6000),
    info: (msg, dur) => push("info", msg, dur),
    dismiss,
  };

  const ICON = {
    success: <FiCheckCircle size={18} />,
    error: <FiAlertTriangle size={18} />,
    warning: <FiAlertTriangle size={18} />,
    info: <FiInfo size={18} />,
  };

  return (
    <ToastContext.Provider value={api}>
      {children}

      {/* Toast container — fixed bottom-right */}
      {toasts.length > 0 && (
        <div className="toast-container" role="status" aria-live="polite">
          {toasts.map((t) => (
            <div key={t.id} className={`toast toast-${t.level}`}>
              <span className="toast-icon">{ICON[t.level]}</span>
              <span className="toast-msg">{t.message}</span>
              <button
                className="toast-close"
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss notification"
              >
                <FiX size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}
